import React, { useState, useEffect } from 'react';
import { getPackagesByType, PACKAGE_TYPES } from '../services/packageService';
import { getUserActiveSubscription, createSubscription } from '../services/subscriptionService';
import { initiateCashfreePayment, calculateGST, formatAmount } from '../services/cashfreeService';
import { createPaymentRecord, markPaymentSuccess, markPaymentFailed } from '../services/paymentService';
import { createInvoice } from '../services/invoiceService';
import { notifyAndroidPaymentSuccess } from '../services/androidBridge';
import LoadingSpinner from './LoadingSpinner';

// Predefined package features for comparison
const PREDEFINED_EMPLOYER_FEATURES = [
  'Unlimited Job Applications',
  '10 Days Job Validity',
  '15 Days Job Validity',
  '30 Days Job Validity',
  '60 Days Job Posting',
  '1 Job Posting',
  '2 Job Posting',
  '3 Job Posting',
  '10 Job Postings',
  'Premium Listing Position',
  '5 Pre Screened Leads',
  '20 Database Candidates'
];

const EmployerPackagePurchase = ({ user, userData, onPurchaseSuccess, showCurrentPackage = true }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('🏢 EmployerPackagePurchase: Fetching EMPLOYER and DATABASE packages');

      // Fetch employer packages
      const employerPackagesResult = await getPackagesByType(PACKAGE_TYPES.EMPLOYER);

      // Fetch database packages
      const databasePackagesResult = await getPackagesByType('database');

      const allPackages = [];

      if (employerPackagesResult.success) {
        console.log(`✅ Found ${employerPackagesResult.packages.length} employer packages`);
        allPackages.push(...employerPackagesResult.packages);
      }

      if (databasePackagesResult.success) {
        console.log(`✅ Found ${databasePackagesResult.packages.length} database packages`);
        allPackages.push(...databasePackagesResult.packages);
      }

      console.log('📦 Total packages for employers:', allPackages.map(p => ({ name: p.name, type: p.type })));
      setPackages(allPackages);

      // Fetch current subscription
      if (user?.uid) {
        const subscriptionResult = await getUserActiveSubscription(user.uid);
        if (subscriptionResult.success && subscriptionResult.subscription) {
          console.log('✅ Found active subscription:', subscriptionResult.subscription.packageName);
          setCurrentSubscription(subscriptionResult.subscription);
        } else {
          console.log('ℹ️ No active subscription found');
        }
      }
    } catch (error) {
      console.error('❌ Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setShowPaymentModal(true);
  };

  const handlePurchase = async (pkg) => {
    if (!user?.uid) {
      alert('Please login to purchase a package');
      return;
    }

    setPurchasing(pkg.id);

    try {
      // Helper to extract numeric limits from features if direct property is missing
      const extractLimitFromFeatures = (features, pattern) => {
        if (!features || !Array.isArray(features)) return 0;
        for (const feature of features) {
          const match = feature.match(pattern);
          if (match && match[1]) {
            return parseInt(match[1], 10);
          }
        }
        return 0;
      };

      // Ensure we have valid limits, falling back to feature parsing if needed
      const maxJobPostings = pkg.maxJobPostings || extractLimitFromFeatures(pkg.features, /(\d+)\s+Job Post/i);
      const featuredJobs = pkg.featuredJobs || extractLimitFromFeatures(pkg.features, /(\d+)\s+Featured Job/i);

      console.log('📦 Resolved Package Limits:', {
        name: pkg.name,
        maxJobPostings,
        featuredJobs,
        originalPostings: pkg.maxJobPostings,
        originalFeatured: pkg.featuredJobs
      });

      // Calculate GST (18%)
      const gstCalculation = calculateGST(pkg.price);
      const baseAmount = gstCalculation.baseAmount;
      const gstAmount = gstCalculation.gstAmount;
      const totalAmount = gstCalculation.totalAmount;

      console.log('💰 Payment Breakdown:', {
        baseAmount,
        gstAmount,
        totalAmount,
        gstRate: '18%'
      });

      // Create payment record in Firebase
      const metadata = {
        packageType: pkg.type,
        gstRate: 18,
      };

      // Add package-specific metadata
      if (pkg.type === 'database') {
        metadata.maxContacts = pkg.maxContacts || 0;
        metadata.durationDays = pkg.durationDays || pkg.duration || 30; // Fallback to duration or 30 days
      } else {
        metadata.duration = pkg.duration || 30;
        metadata.maxJobPostings = maxJobPostings;
        metadata.featuredJobs = featuredJobs;
      }

      const paymentRecordResult = await createPaymentRecord({
        userId: user.uid,
        packageId: pkg.id,
        packageName: pkg.name,
        amount: baseAmount,
        gstAmount: gstAmount,
        totalAmount: totalAmount,
        currency: 'INR',
        userEmail: userData?.email || user.email,
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        userPhone: userData?.phone || '',
        metadata: metadata,
      });

      if (!paymentRecordResult.success) {
        throw new Error('Failed to create payment record');
      }

      const firebasePaymentId = paymentRecordResult.paymentId;

      // Initiate Cashfree payment with total amount (including GST)
      const paymentResult = await initiateCashfreePayment({
        amount: totalAmount,
        baseAmount: baseAmount,
        gstAmount: gstAmount,
        currency: 'INR',
        packageName: pkg.name,
        packageId: pkg.id,
        userEmail: userData?.email || user.email,
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        userPhone: userData?.phone || '',
        employerName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName, // Employer is the user
        employerPhone: userData?.phone || '', // Employer's phone
        onSuccess: async (paymentResponse) => {
          console.log('Payment successful:', paymentResponse);

          // Update payment record
          await markPaymentSuccess(firebasePaymentId, paymentResponse);

          // Create invoice
          const invoiceMetadata = {
            packageType: pkg.type,
          };

          if (pkg.type === 'database') {
            invoiceMetadata.maxContacts = pkg.maxContacts || 0;
            invoiceMetadata.durationDays = pkg.durationDays || pkg.duration || 30;
          } else {
            invoiceMetadata.maxJobPostings = maxJobPostings;
            invoiceMetadata.featuredJobs = featuredJobs;
          }

          const invoiceResult = await createInvoice({
            userId: user.uid,
            paymentId: firebasePaymentId,
            cashfreePaymentId: paymentResponse.paymentId,
            cashfreeOrderId: paymentResponse.orderId,
            customerName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
            customerEmail: userData?.email || user.email,
            customerPhone: userData?.phone || '',
            packageId: pkg.id,
            packageName: pkg.name,
            packageDuration: pkg.type === 'database' ? (pkg.durationDays || pkg.duration || 30) : pkg.duration,
            baseAmount: baseAmount,
            gstRate: 18,
            gstAmount: gstAmount,
            totalAmount: totalAmount,
            currency: 'INR',
            metadata: invoiceMetadata,
          });

          if (invoiceResult.success) {
            console.log('✅ Invoice created:', invoiceResult.invoiceNumber);
          } else {
            console.error('❌ Failed to create invoice:', invoiceResult.error);
          }

          // Handle database packages differently
          if (pkg.type === 'database') {
            // Import and use database access service
            const { grantDatabaseAccess } = await import('../services/databaseAccessService');

            const databaseAccessResult = await grantDatabaseAccess(user.uid, {
              id: pkg.id,
              name: pkg.name,
              maxContacts: pkg.maxContacts || 0,
              durationDays: pkg.durationDays || pkg.duration || 30,
            });

            if (databaseAccessResult.success) {
              const validityDays = pkg.durationDays || pkg.duration || 30;
              notifyAndroidPaymentSuccess(pkg.name);
              if (onPurchaseSuccess) {
                onPurchaseSuccess({ ...pkg, type: 'database' });
              }
            } else {
              alert('Payment successful but failed to activate database access. Please contact support.');
            }
          } else {
            // Create subscription for regular packages
            const subscriptionData = {
              userId: user.uid,
              packageId: pkg.id,
              packageName: pkg.name,
              packageType: pkg.type,
              price: pkg.price,
              duration: pkg.duration,
              features: pkg.features || [],
              maxJobPostings: maxJobPostings,
              featuredJobs: featuredJobs,
              maxApplications: 0,
              resumeTemplates: 0,
              candidateSearch: pkg.candidateSearch || false,
              analyticsAccess: pkg.analyticsAccess || false,
              userType: 'employer',
              paymentId: firebasePaymentId,
              cashfreePaymentId: paymentResponse.paymentId,
              cashfreeOrderId: paymentResponse.orderId || null,
            };

            const subscriptionResult = await createSubscription(subscriptionData);

            if (subscriptionResult.success) {
              // Immediately update local state to reflect new limits
              setCurrentSubscription(subscriptionResult.subscription);

              if (subscriptionResult.extended) {
                notifyAndroidPaymentSuccess(pkg.name);
              } else {
                notifyAndroidPaymentSuccess(pkg.name);
              }

              if (onPurchaseSuccess) {
                onPurchaseSuccess(subscriptionResult.subscription);
              }
            } else {
              alert('Payment successful but failed to activate subscription. Please contact support.');
            }
          }

          setShowPaymentModal(false);
          setSelectedPackage(null);
          // Do NOT fetch data immediately to avoid stale reads overwriting our optimistic update.
          // The local state update above (setCurrentSubscription) is sufficient and accurate.
          setPurchasing(null);
        },
        onFailure: async (error) => {
          console.error('Payment failed:', error);

          // Update payment record
          await markPaymentFailed(firebasePaymentId, error.error || 'Payment failed');

          if (!error.cancelled) {
            alert(`Payment failed: ${error.error || 'Unknown error'}`);
          }

          setPurchasing(null);
        },
      });

      if (!paymentResult.success) {
        throw new Error(paymentResult.error);
      }
    } catch (error) {
      console.error('Error purchasing package:', error);
      alert('Error initiating payment: ' + error.message);
      setPurchasing(null);
    }
  };

  const isCurrentPackage = (pkg) => {
    // Check if this package was the last purchased one
    if (!currentSubscription || !currentSubscription.purchaseHistory) return false;
    const lastPurchase = currentSubscription.purchaseHistory[currentSubscription.purchaseHistory.length - 1];
    return lastPurchase && lastPurchase.packageId === pkg.id;
  };

  const getSubscriptionStatus = () => {
    if (!currentSubscription) return null;

    const endDate = currentSubscription.endDate.toDate();
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

    return {
      packageName: currentSubscription.packageName,
      daysLeft,
      isExpiring: daysLeft <= 7,
      usageStats: currentSubscription.usageStats || {},
      maxJobPostings: currentSubscription.maxJobPostings || 0,
      featuredJobs: currentSubscription.featuredJobs || 0,
    };
  };

  if (loading) return <LoadingSpinner />;

  const subscriptionStatus = getSubscriptionStatus();

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Employer Packages</h1>
        <p className="text-gray-600">
          Choose a package to start posting jobs and accessing premium features
        </p>
      </div>

      {/* Current Subscription Status */}
      {showCurrentPackage && subscriptionStatus && (
        <div className={`mb-8 p-6 rounded-2xl border-2 ${subscriptionStatus.isExpiring
          ? 'bg-yellow-50 border-yellow-300'
          : 'bg-green-50 border-green-300'
          }`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-3 h-3 rounded-full ${subscriptionStatus.isExpiring ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'
                  }`}></div>
                <h3 className={`text-xl font-bold ${subscriptionStatus.isExpiring ? 'text-yellow-900' : 'text-green-900'
                  }`}>
                  Current Package: {subscriptionStatus.packageName}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">Days Remaining</p>
                  <p className={`text-2xl font-bold ${subscriptionStatus.isExpiring ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                    {subscriptionStatus.daysLeft > 0 ? subscriptionStatus.daysLeft : 'Expired'}
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">Job Postings Used</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {subscriptionStatus.usageStats.jobPostingsUsed || 0} / {subscriptionStatus.maxJobPostings}
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">Featured Jobs Used</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {subscriptionStatus.usageStats.featuredJobsUsed || 0} / {subscriptionStatus.featuredJobs}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">⭐ Subset of total postings</p>
                </div>
              </div>
            </div>

            {subscriptionStatus.isExpiring && (
              <div className="ml-4">
                <span className="px-4 py-2 bg-yellow-500 text-white rounded-full text-sm font-semibold">
                  ⚠️ Expiring Soon
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Package Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {packages.map((pkg, index) => {
          return (
            <div
              key={pkg.id}
              className={`relative bg-gradient-to-br from-white via-purple-50/30 to-indigo-50/30 rounded-2xl shadow-xl border-2 transition-all duration-300 hover:shadow-2xl hover:scale-105 ${index === 1
                ? 'border-indigo-500 ring-4 ring-indigo-100 transform scale-105'
                : 'border-gray-200 hover:border-indigo-300'
                }`}
            >
              {/* Popular Badge */}
              {index === 1 && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    ⭐ RECOMMENDED
                  </span>
                </div>
              )}

              {/* Last Purchased Badge */}
              {isCurrentPackage(pkg) && (
                <div className="absolute -top-4 right-4 z-10">
                  <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    ✓ LAST PURCHASED
                  </span>
                </div>
              )}

              <div className="p-8">
                {/* Package Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{pkg.name}</h3>

                  {/* Database Package Badge */}
                  {pkg.type === 'database' && (
                    <div className="mb-4">
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                        🔓 Database Access Package
                      </span>
                    </div>
                  )}

                  {/* Price Breakdown */}
                  <div className="mb-4">
                    <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-4 mb-3">
                      <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
                        <span>Base Price:</span>
                        <span className="font-semibold">{formatAmount(pkg.price)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
                        <span>GST (18%):</span>
                        <span className="font-semibold">{formatAmount(calculateGST(pkg.price).gstAmount)}</span>
                      </div>
                      <div className="border-t border-gray-300 pt-2 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="text-base font-bold text-gray-900">Total Amount:</span>
                          <span className="text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                            {formatAmount(calculateGST(pkg.price).totalAmount)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Database Package Info */}
                    {pkg.type === 'database' ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center gap-2 text-green-600">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                          </svg>
                          <span className="font-bold text-lg">{pkg.maxContacts} Contacts</span>
                        </div>
                        <span className="text-gray-500 text-sm block">
                          Valid for {pkg.durationDays || 30} days
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">
                        Valid for {pkg.duration} days
                      </span>
                    )}
                  </div>

                  {pkg.description && (
                    <p className="text-sm text-gray-600 mt-4">{pkg.description}</p>
                  )}
                </div>

                {/* Features - Show only included features */}
                <div className="mb-8">
                  {pkg.features && pkg.features.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-green-50">
                        <div className="px-3 py-2 bg-green-100">
                          <p className="text-xs font-semibold text-green-800">✓ Package Features ({pkg.features.length})</p>
                        </div>
                        <div className="p-2 space-y-1" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          {pkg.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center justify-between px-3 py-2 bg-white rounded">
                              <span className="text-xs text-gray-800">{feature}</span>
                              <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Purchase Button */}
                <button
                  onClick={() => handlePurchase(pkg)}
                  disabled={purchasing === pkg.id}
                  className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${purchasing === pkg.id
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : index === 1
                      ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 shadow-xl'
                      : 'bg-gradient-to-r from-gray-800 to-gray-900 text-white hover:from-gray-700 hover:to-gray-800 shadow-lg'
                    }`}
                >
                  {purchasing === pkg.id
                    ? '🔄 Processing...'
                    : isCurrentPackage(pkg)
                      ? 'Purchase Again'
                      : currentSubscription
                        ? `Get ${pkg.name}`
                        : `Get ${pkg.name}`
                  }
                </button>

                {/* Info text for accumulation */}
                {currentSubscription && (
                  <p className="text-xs text-center text-gray-600 mt-2">
                    ✨ Benefits will be added to your current subscription
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {packages.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Packages Available</h3>
          <p className="text-gray-600">
            No employer packages are currently available. Please check back later.
          </p>
        </div>
      )}

      {/* Payment Info */}
      <div className="mt-12 bg-blue-50 border border-blue-200 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-blue-900 mb-2">Secure Payment with Cashfree</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Secure payment gateway powered by Cashfree</li>
              <li>• Instant activation after successful payment</li>
              <li>• Support for UPI, Cards, Net Banking, and Wallets</li>
              <li>• 100% secure and encrypted transactions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerPackagePurchase;
